import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Routes, Route, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, MessagesSquare, CreditCard, FileText, Inbox, LogOut, Sparkles, Send, Save, Paperclip, X, Check, Clock, Download, RefreshCw, ArrowRight, Filter, KeyRound, Eye, EyeOff, Copy, Archive, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

const CATS = ["Rental property", "GST", "Vehicle expenses", "Bright-line / property", "Home office", "Sole trader / contractor", "Other"];
const STAGE_LABELS = { new: "New", drafted: "Drafted", approved: "Approved", sent: "Sent" };

function StagePill({ s }) {
  const cls = s === "new" ? "pill-new" : s === "drafted" ? "pill-draft" : s === "approved" ? "pill-approved" : "pill-sent";
  return <span className={`pill ${cls}`} data-testid={`stage-${s}`}>{STAGE_LABELS[s]}</span>;
}

const Sidebar = ({ user, unread }) => {
  const { logout } = useAuth();
  const nav = useNavigate();
  const items = [
    { to: "/admin", label: "Dashboard", ic: LayoutDashboard, end: true },
    { to: "/admin/clients", label: "Clients", ic: Users },
    { to: "/admin/queries", label: "Queries", ic: MessagesSquare },
    { to: "/admin/payments", label: "Payments", ic: CreditCard },
    { to: "/admin/invoices", label: "Invoices", ic: FileText },
    { to: "/admin/backups", label: "Backups", ic: Archive },
    { to: "/admin/support", label: "Support", ic: Inbox, badge: unread },
  ];
  return (
    <aside className="at-sidebar" data-testid="admin-sidebar">
      <div className="at-side-logo">Ask<b>Tax</b> <span className="text-xs opacity-50 font-normal ml-1">Admin</span></div>
      <div className="p-3 mb-4 border" style={{ borderColor: "var(--kiwi)", background: "rgba(47,125,81,0.15)", borderRadius: 3 }}>
        <div className="text-xs uppercase font-display font-bold tracking-widest" style={{ color: "var(--kiwi-2)" }}>Signed in</div>
        <div className="mt-1 text-sm text-white truncate">{user?.email}</div>
        <div className="text-xs opacity-60">{user?.role}</div>
      </div>
      {items.map(({ to, label, ic: Ic, end, badge }) => (
        <NavLink key={to} to={to} end={end} className={({ isActive }) => `at-side-item ${isActive ? "active" : ""}`} data-testid={`admin-nav-${label.toLowerCase()}`}>
          <Ic size={16} /> {label}
          {badge > 0 && <span className="at-side-badge">{badge}</span>}
        </NavLink>
      ))}
      <div className="mt-8 pt-4 border-t border-gray-700">
        <NavLink to="/change-password" className="at-side-item w-full" data-testid="admin-nav-change-password">
          <KeyRound size={16} /> Change password
        </NavLink>
        <button onClick={() => { logout(); nav("/"); }} className="at-side-item w-full" data-testid="admin-logout">
          <LogOut size={16} /> Sign out
        </button>
      </div>
    </aside>
  );
};

function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [qs, setQs] = useState([]);
  useEffect(() => {
    api.get("/admin/dashboard").then(r => setD(r.data));
    api.get("/admin/queries").then(r => setQs(r.data.filter(q => q.stage !== "sent").slice(0, 6)));
  }, []);
  if (!d) return null;
  const firstName = (user?.name || "").split(" ")[0] || "there";
  return (
    <>
      <div className="at-main-head">
        <div><span className="eyebrow">Answer desk</span><h1 className="at-h1" data-testid="admin-greeting">Good morning, <em>{firstName}</em></h1></div>
      </div>
      <div className="at-stats">
        <div className="at-stat"><div className="at-stat-l">Open queries</div><div className="at-stat-v" data-testid="dash-open">{d.open_queries}</div><div className="at-stat-s">Needing attention</div></div>
        <div className="at-stat"><div className="at-stat-l">Answered · month</div><div className="at-stat-v">{d.answered_month}</div><div className="at-stat-s up">Sent this month</div></div>
        <div className="at-stat"><div className="at-stat-l">Revenue · month</div><div className="at-stat-v"><span className="cur">$</span>{d.revenue_month}</div><div className="at-stat-s up">{d.revenue_count} payments</div></div>
        <div className="at-stat"><div className="at-stat-l">Unpaid invoices</div><div className="at-stat-v">{d.unpaid_invoices}</div><div className="at-stat-s">Outstanding</div></div>
      </div>
      <div className="at-card">
        <div className="at-card-head"><div className="at-card-title">Queries needing attention</div></div>
        <table className="at-tbl">
          <thead><tr><th>Client</th><th>Category</th><th>Question</th><th>Received</th><th>Stage</th></tr></thead>
          <tbody>
            {qs.map(q => (
              <tr key={q.id}>
                <td className="font-bold">{q.client_name}<div className="text-xs" style={{ color: "var(--muted)" }}>{q.client_company}</div></td>
                <td><span className="at-tag">{q.category}</span></td>
                <td className="max-w-[320px] truncate" style={{ color: "var(--ink-2)" }}>{q.question_text}</td>
                <td style={{ color: "var(--muted)" }}>{new Date(q.created_at).toLocaleDateString("en-NZ")}</td>
                <td><StagePill s={q.stage} /></td>
              </tr>
            ))}
            {qs.length === 0 && <tr><td colSpan={5} className="p-8 text-center font-serif italic" style={{ color: "var(--muted)" }}>Nothing waiting.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Clients() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [revealed, setRevealed] = useState(null);
  const refresh = () => api.get("/admin/clients").then(r => setRows(r.data));
  useEffect(() => { refresh(); }, []);
  const resetPw = async (r) => {
    if (!window.confirm(`Reset password for ${r.email}?\n\n• A new random password will be generated\n• All existing sessions for this user will be signed out\n• You'll see the new password once — copy it before closing`)) return;
    setBusy(r.id);
    try {
      const resp = await api.post(`/admin/clients/${r.id}/reset-password`);
      setRevealed({ email: r.email, name: r.name, password: resp.data.new_password });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setBusy(null); }
  };
  const copyPw = async () => {
    try { await navigator.clipboard.writeText(revealed.password); toast.success("Copied"); } catch { toast.error("Copy failed — select the value manually"); }
  };
  return (
    <>
      <div className="at-main-head"><div><span className="eyebrow">Directory</span><h1 className="at-h1">Clients</h1></div></div>
      <div className="at-card">
        <table className="at-tbl">
          <thead><tr><th>Client</th><th>Email</th><th>Questions</th><th>Total spent</th><th>Last active</th><th></th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} data-testid={`client-${r.email}`}>
                <td className="font-bold">{r.name}<div className="text-xs" style={{ color: "var(--muted)" }}>{r.company}</div></td>
                <td style={{ color: "var(--muted)" }}>{r.email}</td>
                <td className="font-bold">{r.question_count}</td>
                <td className="font-bold">${r.total_spent.toFixed(2)}</td>
                <td style={{ color: "var(--muted)" }}>{r.last_active ? new Date(r.last_active).toLocaleDateString("en-NZ") : "—"}</td>
                <td className="text-right"><button onClick={() => resetPw(r)} disabled={busy === r.id} className="at-btn ghost xs" data-testid={`reset-pw-${r.email}`}><KeyRound size={12} /> {busy === r.id ? "Resetting…" : "Reset password"}</button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center font-serif italic" style={{ color: "var(--muted)" }}>No clients yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {revealed && (
        <div className="overlay" onClick={() => setRevealed(null)} data-testid="pw-reveal-modal">
          <div className="sheet" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="sheet-head">
              <div className="sheet-title">Password <em>reset</em></div>
              <button onClick={() => setRevealed(null)}><X size={22} /></button>
            </div>
            <div className="sheet-body">
              <p className="font-serif text-base">New password for <b>{revealed.name || revealed.email}</b>:</p>
              <div className="mt-3 p-4 border-2 rounded font-mono text-lg break-all" style={{ borderColor: "var(--ink)", background: "var(--cream-3)" }} data-testid="pw-reveal-value">{revealed.password}</div>
              <div className="mt-4 flex gap-2">
                <button onClick={copyPw} className="at-btn" data-testid="pw-reveal-copy"><Copy size={14} /> Copy to clipboard</button>
                <button onClick={() => setRevealed(null)} className="at-btn ghost">Done</button>
              </div>
              <div className="mt-4 p-3 border-2 rounded text-xs" style={{ borderColor: "var(--clay)", background: "#f4e0d8", color: "var(--clay)" }}>
                <b>This won't be shown again.</b> All existing sessions for this user have been signed out — they must sign in fresh with this password. Share it via a secure channel.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---- QUERIES: cockpit / list / kanban ----
function Queries() {
  const [rows, setRows] = useState([]);
  const [view, setView] = useState("cockpit");
  const [cat, setCat] = useState("all");
  const [stg, setStg] = useState("all");
  const [selId, setSelId] = useState(null);
  const load = () => api.get("/admin/queries").then(r => { setRows(r.data); if (!selId && r.data.length) setSelId(r.data[0].id); });
  useEffect(() => { load(); }, []);
  const filtered = useMemo(() => rows.filter(r => (cat === "all" || r.category === cat) && (stg === "all" || r.stage === stg)), [rows, cat, stg]);
  const sel = rows.find(r => r.id === selId) || filtered[0];
  const bulkable = cat !== "all" && filtered.some(r => r.stage === "new");
  const bulkNew = filtered.filter(r => r.stage === "new").length;

  const bulkDraft = async () => {
    try { await api.post(`/admin/queries/bulk-draft?category=${encodeURIComponent(cat)}`); toast.success("Drafts generated"); load(); }
    catch { toast.error("Bulk draft failed"); }
  };

  return (
    <>
      <div className="at-main-head">
        <div><span className="eyebrow">Answer desk</span><h1 className="at-h1">Queries</h1></div>
      </div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <div className="vtoggle">
          <button className={`vt-btn ${view === "cockpit" ? "on" : ""}`} onClick={() => setView("cockpit")} data-testid="view-cockpit">Cockpit</button>
          <button className={`vt-btn ${view === "list" ? "on" : ""}`} onClick={() => setView("list")} data-testid="view-list">List</button>
          <button className={`vt-btn ${view === "kanban" ? "on" : ""}`} onClick={() => setView("kanban")} data-testid="view-kanban">Kanban</button>
        </div>
        <div className="flex items-center gap-2">
          <select className="at-select" style={{ width: "auto" }} value={cat} onChange={e => setCat(e.target.value)} data-testid="filter-cat">
            <option value="all">All categories</option>
            {CATS.map(c => <option key={c}>{c}</option>)}
          </select>
          <select className="at-select" style={{ width: "auto" }} value={stg} onChange={e => setStg(e.target.value)} data-testid="filter-stage">
            <option value="all">All stages</option>
            <option value="new">New</option><option value="drafted">Drafted</option><option value="approved">Approved</option><option value="sent">Sent</option>
          </select>
          <span className="text-xs font-display font-bold uppercase tracking-widest" style={{ color: "var(--muted)" }}>{filtered.length} of {rows.length}</span>
        </div>
      </div>
      {bulkable && bulkNew > 0 && (
        <div className="mb-4 at-card p-4 flex justify-between items-center" style={{ background: "#fce5c6" }} data-testid="bulk-bar">
          <div className="flex items-center gap-2"><Sparkles size={16} /> <span className="font-display font-bold uppercase tracking-wide text-sm">{bulkNew} new in {cat}</span></div>
          <button className="at-btn amber sm" onClick={bulkDraft} data-testid="bulk-draft">Generate all drafts</button>
        </div>
      )}
      {view === "cockpit" && <Cockpit rows={filtered} sel={sel} onSelect={setSelId} onChanged={load} />}
      {view === "list" && <ListView rows={filtered} onOpen={(id) => { setSelId(id); setView("cockpit"); }} />}
      {view === "kanban" && <Kanban rows={filtered} onOpen={(id) => { setSelId(id); setView("cockpit"); }} onChanged={load} />}
    </>
  );
}

function Cockpit({ rows, sel, onSelect, onChanged }) {
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const fileRef = useRef(null);
  useEffect(() => { setDraft(sel?.draft_answer || ""); }, [sel?.id, sel?.draft_answer]);

  if (!sel) return <div className="at-card p-12 text-center font-serif italic" style={{ color: "var(--muted)" }}>Nothing matches these filters.</div>;

  const stage = sel.stage;
  const doAction = async (fn, msg) => {
    setBusy(true); try { await fn(); toast.success(msg); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const gen = () => doAction(() => api.post(`/admin/queries/${sel.id}/generate-draft`), "Draft generated");
  const save = () => doAction(() => api.put(`/admin/queries/${sel.id}/draft`, { draft_answer: draft }), "Draft saved");
  const approve = () => doAction(async () => { await api.put(`/admin/queries/${sel.id}/draft`, { draft_answer: draft }); await api.post(`/admin/queries/${sel.id}/approve`); }, "Approved — ready to send");
  const send = () => doAction(async () => { await api.put(`/admin/queries/${sel.id}/draft`, { draft_answer: draft }); await api.post(`/admin/queries/${sel.id}/send`); }, "Answer sent to client");

  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/admin/queries/${sel.id}/attach`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Attached ${file.name}`); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };
  const onDrop = (e) => { e.preventDefault(); [...e.dataTransfer.files].forEach(upload); };
  const removeFile = async (fid) => {
    try { await api.delete(`/admin/queries/${sel.id}/attach/${fid}`); onChanged(); } catch { toast.error("Could not remove"); }
  };

  return (
    <div className="grid md:grid-cols-[300px_1fr] gap-4" data-testid="cockpit">
      <div className="at-card p-2 max-h-[80vh] overflow-y-auto" data-testid="cockpit-list">
        {rows.map(r => (
          <div key={r.id} onClick={() => onSelect(r.id)} className="p-3 border-b cursor-pointer" style={{ borderColor: "var(--hair)", background: r.id === sel.id ? "var(--cream-3)" : "transparent" }} data-testid={`cockpit-item-${r.ref}`}>
            <div className="flex justify-between items-start mb-1">
              <span className="text-xs font-display font-bold uppercase tracking-widest" style={{ color: "var(--muted)" }}>{r.ref}</span>
              <StagePill s={r.stage} />
            </div>
            <div className="text-xs font-bold uppercase tracking-wide" style={{ color: "var(--kiwi)" }}>{r.category}</div>
            <div className="text-sm mt-1 font-serif italic line-clamp-2">{r.question_text}</div>
            <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>{r.client_name}</div>
          </div>
        ))}
      </div>
      <div className="at-card p-6" data-testid="cockpit-editor">
        <div className="stagebar">
          {["new", "drafted", "approved", "sent"].map((s, i) => {
            const order = { new: 0, drafted: 1, approved: 2, sent: 3 };
            const done = order[stage] > i;
            const active = order[stage] === i;
            return (
              <div key={s} className="flex items-center flex-1 last:flex-initial">
                <div className={`stg ${done ? "done" : ""} ${active ? "active" : ""}`}>
                  <div className="stg-dot">{done ? <Check size={14} /> : i + 1}</div>
                  {STAGE_LABELS[s]}
                </div>
                {i < 3 && <div className={`stg-line ${done ? "done" : ""} flex-1 mx-2`}></div>}
              </div>
            );
          })}
        </div>
        <div className="flex justify-between items-start mb-4 pb-4 border-b" style={{ borderColor: "var(--hair)" }}>
          <div>
            <div className="text-xs font-display font-bold uppercase tracking-widest" style={{ color: "var(--muted)" }}>{sel.ref}</div>
            <div className="mt-1 font-display font-black uppercase text-lg">{sel.client_name}</div>
            <div className="text-xs" style={{ color: "var(--muted)" }}>{sel.client_email} · {sel.client_company}</div>
          </div>
          <div className="text-right">
            <span className="at-tag" style={{ background: "var(--kiwi)", color: "var(--cream-2)" }}>{sel.category}</span>
          </div>
        </div>
        <div className="p-4 border-2 rounded" style={{ borderColor: "var(--ink)", background: "var(--cream-3)" }}>
          <div className="text-xs font-display font-bold uppercase tracking-widest" style={{ color: "var(--muted)" }}>Client's question</div>
          <div className="mt-2 font-serif italic text-lg leading-relaxed">{sel.question_text}</div>
        </div>

        <div className="mt-6">
          <div className="flex justify-between items-center mb-2">
            <label className="at-label mb-0">Draft & response editor <span className="ml-2 text-xs font-serif italic normal-case tracking-normal" style={{ color: "var(--muted)" }}>· markdown supported</span></label>
            <div className="flex gap-2">
              <button
                onClick={() => setShowPreview(v => !v)}
                className="at-btn ghost xs"
                data-testid="btn-toggle-preview"
                title="Toggle live client-view preview"
              >
                {showPreview ? <><EyeOff size={12} /> Hide preview</> : <><Eye size={12} /> Show preview</>}
              </button>
              <button onClick={gen} className="at-btn ghost xs" disabled={busy || stage === "sent"} data-testid="btn-generate"><Sparkles size={12} /> {sel.draft_answer ? "Regenerate" : "Generate draft"}</button>
            </div>
          </div>
          <div className={showPreview ? "grid md:grid-cols-2 gap-3" : ""}>
            <textarea className="at-textarea" style={{ minHeight: 260, background: stage === "sent" ? "var(--cream-3)" : "var(--cream-2)" }} value={draft} onChange={e => setDraft(e.target.value)} readOnly={stage === "sent"} placeholder="Click 'Generate draft' or type the answer manually. Markdown supported (# heading, **bold**, - bullets, 1. numbered)." data-testid="draft-editor" />
            {showPreview && (
              <div
                className="markdown-answer p-4 border-2 rounded font-serif overflow-y-auto"
                style={{ minHeight: 260, maxHeight: 520, borderColor: "var(--ink)", background: "var(--cream)" }}
                data-testid="draft-preview"
              >
                <div className="text-[10px] font-display font-bold uppercase tracking-widest mb-3 pb-2 border-b border-dashed" style={{ color: "var(--muted-2)", borderColor: "var(--hair)" }}>Client view · live preview</div>
                {draft.trim()
                  ? <ReactMarkdown>{draft}</ReactMarkdown>
                  : <div className="text-xs italic" style={{ color: "var(--muted)" }}>Start typing markdown on the left — you'll see exactly what the client will read here.</div>}
              </div>
            )}
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>{draft.length} chars · {draft.trim().split(/\s+/).filter(Boolean).length} words</div>
        </div>

        {stage !== "sent" && (
          <div className="mt-4 flex gap-2 flex-wrap">
            <button onClick={save} className="at-btn ghost" disabled={busy || !draft}><Save size={14} /> Save draft</button>
            {stage === "drafted" && <button onClick={approve} className="at-btn amber" disabled={busy || !draft} data-testid="btn-approve"><Check size={14} /> Approve draft</button>}
            {stage === "approved" && <button onClick={send} className="at-btn" disabled={busy || !draft} data-testid="btn-send"><Send size={14} /> Send answer & complete</button>}
            {stage === "drafted" && <button onClick={send} className="at-btn ghost" disabled={busy || !draft} data-testid="btn-quicksend">Send now →</button>}
          </div>
        )}

        <div className="mt-6 pt-6 border-t-2" style={{ borderColor: "var(--ink)" }}>
          <div className="at-label">Files for the client {stage === "sent" && <span className="ml-2 text-xs" style={{ color: "var(--kiwi)" }}>Delivered</span>}</div>
          {stage !== "sent" && (
            <div className="attach-drop" onClick={() => fileRef.current.click()} onDragOver={(e) => e.preventDefault()} onDrop={onDrop} data-testid="attach-drop">
              <Paperclip className="mx-auto" />
              <div className="mt-2 font-display font-bold uppercase tracking-wide text-sm">Click or drop files</div>
              <div className="text-xs" style={{ color: "var(--muted)" }}>PDF, images, spreadsheets · 15 MB max</div>
              <input ref={fileRef} type="file" multiple hidden onChange={(e) => [...e.target.files].forEach(upload)} data-testid="attach-input" />
            </div>
          )}
          {sel.answer_files && sel.answer_files.length > 0 && (
            <div className="mt-3 space-y-2">
              {sel.answer_files.map(f => (
                <div key={f.id} className="p-3 border-2 rounded flex justify-between items-center" style={{ borderColor: "var(--ink)", background: "var(--cream-2)" }}>
                  <div className="flex items-center gap-3"><FileText size={16} /> <span className="text-sm">{f.name}</span> <span className="text-xs" style={{ color: "var(--muted)" }}>{Math.round(f.size / 1024)} KB</span></div>
                  {stage !== "sent" && <button onClick={() => removeFile(f.id)} data-testid={`remove-file-${f.id}`}><X size={14} /></button>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 pt-4 border-t border-dashed text-xs font-serif italic" style={{ borderColor: "var(--hair)", color: "var(--muted)" }}>
          Editing here changes only the draft. The client sees nothing until you <b>Send</b>.
        </div>
      </div>
    </div>
  );
}

function ListView({ rows, onOpen }) {
  return (
    <div className="at-card">
      <table className="at-tbl">
        <thead><tr><th>Ref</th><th>Client</th><th>Category</th><th>Question</th><th>Received</th><th>Stage</th><th></th></tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id}>
              <td className="font-bold">{r.ref}</td>
              <td>{r.client_name}</td>
              <td><span className="at-tag">{r.category}</span></td>
              <td className="max-w-[320px] truncate" style={{ color: "var(--ink-2)" }}>{r.question_text}</td>
              <td style={{ color: "var(--muted)" }}>{new Date(r.created_at).toLocaleDateString("en-NZ")}</td>
              <td><StagePill s={r.stage} /></td>
              <td><button className="at-btn ghost xs" onClick={() => onOpen(r.id)} data-testid={`list-open-${r.ref}`}>Open <ArrowRight size={12} /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Kanban({ rows, onOpen, onChanged }) {
  const [drag, setDrag] = useState(null);
  const cols = ["new", "drafted", "approved", "sent"];
  const move = async (qid, stage) => {
    try { await api.post(`/admin/queries/${qid}/stage?stage=${stage}`); toast.success(`Moved to ${stage}`); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Move blocked"); }
  };
  return (
    <div className="kanban" data-testid="kanban">
      {cols.map(c => (
        <div key={c} className="kanban-col" onDragOver={(e) => e.preventDefault()} onDrop={() => drag && move(drag, c)} data-testid={`kanban-col-${c}`}>
          <div className="kanban-col-head">{STAGE_LABELS[c]} <span>{rows.filter(r => r.stage === c).length}</span></div>
          {rows.filter(r => r.stage === c).map(r => (
            <div key={r.id} draggable className="kanban-card" onDragStart={() => setDrag(r.id)} onDragEnd={() => setDrag(null)} onClick={() => onOpen(r.id)} data-testid={`kanban-card-${r.ref}`}>
              <div className="text-xs font-display font-bold uppercase tracking-widest" style={{ color: "var(--muted)" }}>{r.ref}</div>
              <div className="text-xs font-bold uppercase tracking-wide mt-1" style={{ color: "var(--kiwi)" }}>{r.category}</div>
              <div className="text-sm mt-1 font-serif italic line-clamp-3">{r.question_text}</div>
              <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>{r.client_name}</div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function AdminPayments() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/payments").then(r => setRows(r.data)); }, []);
  return (
    <>
      <div className="at-main-head"><div><span className="eyebrow">Stripe</span><h1 className="at-h1">Payments</h1></div></div>
      <div className="at-card">
        <table className="at-tbl">
          <thead><tr><th>Client</th><th>Item</th><th>Amount</th><th>Method</th><th>Date</th><th>Status</th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td className="font-bold">{r.client_name}</td>
                <td>{r.item}</td>
                <td className="font-bold">${r.amount.toFixed(2)}</td>
                <td style={{ color: "var(--muted)" }}>{r.method}</td>
                <td style={{ color: "var(--muted)" }}>{new Date(r.created_at).toLocaleDateString("en-NZ")}</td>
                <td><span className="pill pill-paid">{r.status}</span></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center font-serif italic" style={{ color: "var(--muted)" }}>No payments yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

function AdminInvoices() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/invoices").then(r => setRows(r.data)); }, []);
  const dl = async (no) => {
    const r = await api.get(`/invoices/${no}/pdf`, { responseType: "blob" });
    const blob = new Blob([r.data], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${no}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <>
      <div className="at-main-head"><div><span className="eyebrow">GST invoices</span><h1 className="at-h1">Invoices</h1></div></div>
      <div className="at-card">
        <table className="at-tbl">
          <thead><tr><th>Invoice #</th><th>Client</th><th>Amount (incl. GST)</th><th>Issued</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td className="font-bold">{r.invoice_no}</td>
                <td>{r.client_name}</td>
                <td className="font-bold">${r.amount.toFixed(2)} <span className="font-normal" style={{ color: "var(--muted-2)" }}>(incl. ${r.gst_amount.toFixed(2)} GST)</span></td>
                <td style={{ color: "var(--muted)" }}>{new Date(r.created_at).toLocaleDateString("en-NZ")}</td>
                <td><span className="pill pill-paid">{r.status}</span></td>
                <td><button className="at-btn ghost xs" onClick={() => dl(r.invoice_no)}><Download size={12} /> PDF</button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center font-serif italic" style={{ color: "var(--muted)" }}>No invoices yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

function AdminSupport({ refreshUnread }) {
  const [rows, setRows] = useState([]);
  const [selId, setSelId] = useState(null);
  const [reply, setReply] = useState("");
  const [filter, setFilter] = useState("all");
  const replyRef = useRef(null);
  const load = () => api.get("/admin/support").then(r => { setRows(r.data); if (!selId && r.data.length) setSelId(r.data[0].id); refreshUnread(); });
  useEffect(() => { load(); }, []);
  const sel = rows.find(r => r.id === selId);
  const filtered = rows.filter(r => filter === "all" || (filter === "new" && r.status === "new") || (filter === "replied" && r.status === "replied"));

  useEffect(() => {
    if (sel && !sel.read_by_admin) {
      api.post(`/admin/support/${sel.id}/read`).then(load);
    }
    setReply(sel?.admin_reply || "");
  }, [selId]);

  const doReply = async () => {
    if (!reply.trim()) { toast.error("Reply is empty"); return; }
    try {
      await api.post(`/admin/support/${sel.id}/reply`, { admin_reply: reply });
      toast.success("Reply sent");
      setReply("");
      replyRef.current?.blur();
      load();
    } catch { toast.error("Failed"); }
  };
  const doResolve = async () => {
    try {
      await api.post(`/admin/support/${sel.id}/resolve`);
      toast.success("Marked resolved");
      setReply("");
      replyRef.current?.blur();
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <>
      <div className="at-main-head">
        <div><span className="eyebrow">Help desk</span><h1 className="at-h1">Support <em>inbox</em></h1></div>
        <select className="at-select" style={{ width: "auto" }} value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="all">All messages</option>
          <option value="new">New only</option>
          <option value="replied">Replied</option>
        </select>
      </div>
      <div className="grid md:grid-cols-[340px_1fr] gap-4">
        <div className="at-card p-2 max-h-[75vh] overflow-y-auto">
          {filtered.map(m => (
            <div key={m.id} onClick={() => setSelId(m.id)} className="p-3 border-b cursor-pointer" style={{ borderColor: "var(--hair)", background: m.id === selId ? "var(--cream-3)" : "transparent" }} data-testid={`support-item-${m.ref}`}>
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-display font-bold uppercase tracking-widest" style={{ color: "var(--muted)" }}>{m.ref}</span>
                <span className={`pill ${m.status === "new" ? "pill-new" : m.status === "replied" ? "pill-answered" : "pill-paid"}`}>{m.status}</span>
              </div>
              <div className="text-sm font-bold">{m.client_name}</div>
              <div className="text-xs" style={{ color: "var(--muted)" }}>{m.topic}</div>
              <div className="text-sm mt-1 font-serif italic line-clamp-2">{m.message}</div>
              {!m.read_by_admin && <div className="mt-1 text-xs font-display font-bold uppercase tracking-wide" style={{ color: "var(--amber)" }}>● Unread</div>}
            </div>
          ))}
          {filtered.length === 0 && <div className="p-6 text-center font-serif italic" style={{ color: "var(--muted)" }}>No messages.</div>}
        </div>
        <div className="at-card p-6">
          {!sel ? (
            <div className="text-center py-16 font-serif italic" style={{ color: "var(--muted)" }}>Select a message</div>
          ) : (
            <>
              <div className="flex justify-between items-start mb-4 pb-4 border-b" style={{ borderColor: "var(--hair)" }}>
                <div>
                  <div className="font-display font-bold uppercase tracking-wide text-lg">{sel.client_name}</div>
                  <div className="text-xs" style={{ color: "var(--muted)" }}>{sel.client_email}</div>
                  <div className="mt-2"><span className="at-tag">{sel.topic}</span>{sel.linked_query_ref && <span className="at-tag ml-2" style={{ background: "var(--kiwi)", color: "var(--cream-2)" }}>{sel.linked_query_ref}</span>}</div>
                </div>
                <span className={`pill ${sel.status === "new" ? "pill-new" : sel.status === "replied" ? "pill-answered" : "pill-paid"}`}>{sel.status}</span>
              </div>
              <div className="p-4 border-2 rounded" style={{ borderColor: "var(--ink)", background: "var(--cream-3)" }}>
                <div className="font-serif italic">{sel.message}</div>
              </div>
              {sel.status === "resolved" ? (
                <div className="mt-6" data-testid="support-resolved-panel">
                  {sel.admin_reply ? (
                    <>
                      <div className="at-label">Your reply <span className="ml-2 text-xs" style={{ color: "var(--kiwi)" }}>Sent · read-only</span></div>
                      <div className="p-4 border-2 rounded" style={{ borderColor: "var(--kiwi)", background: "#e4efe7", borderLeft: "6px solid var(--kiwi)" }} data-testid="support-readonly-reply">
                        <div className="font-serif whitespace-pre-wrap">{sel.admin_reply}</div>
                      </div>
                    </>
                  ) : null}
                  <div className="mt-4 p-3 border-2 rounded flex items-center gap-2 text-sm font-display font-bold uppercase tracking-wide" style={{ borderColor: "var(--ink)", background: "var(--cream-3)" }} data-testid="support-resolved-note">
                    <Check size={14} /> Ticket resolved — closed to new replies.
                  </div>
                </div>
              ) : (
                <>
                  <div className="mt-6">
                    <label className="at-label">Your reply</label>
                    <textarea ref={replyRef} className="at-textarea" value={reply} onChange={e => setReply(e.target.value)} placeholder="Type your reply — this will show in the client's Support page." data-testid="support-reply" />
                  </div>
                  <div className="mt-4 flex gap-2">
                    <button onClick={doReply} className="at-btn" data-testid="support-send-reply"><Send size={14} /> Send reply</button>
                    <button onClick={doResolve} className="at-btn ghost" data-testid="support-resolve">Mark resolved</button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

function Backups() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [data, setData] = useState({ enabled: false, reason: "", items: [], retention_count: 30, s3_configured: false, s3_bucket: null, excluded_collections: [] });
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const refresh = () => api.get("/admin/backups").then(r => setData(r.data));
  useEffect(() => { refresh(); }, []);
  const runNow = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/backup-now");
      toast.success(`Backup complete: ${r.data.manifest.filename.replace("backups/", "")}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backup failed");
    } finally { setBusy(false); }
  };
  const openRestore = (m) => {
    const stem = m.filename.split("/").pop().replace(".json.gz.enc", "");
    setRestoring({ filename: m.filename, stem, expected: `RESTORE ${stem}`, m });
    setConfirmText("");
  };
  const doRestore = async () => {
    if (!restoring || confirmText !== restoring.expected) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/backups/restore", { filename: restoring.filename, confirm: confirmText });
      toast.success(`Restored ${r.data.restored_from.replace("backups/", "")}`);
      setRestoring(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally { setBusy(false); }
  };
  const formatBytes = (b) => {
    if (b == null) return "—";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(2)} MB`;
  };
  const formatDate = (iso) => iso ? new Date(iso).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" }) : "—";
  const rowCountTotal = (rc) => rc ? Object.values(rc).reduce((s, n) => s + n, 0) : 0;
  return (
    <>
      <div className="at-main-head">
        <div><span className="eyebrow">Data durability</span><h1 className="at-h1">Backups</h1></div>
        <button onClick={runNow} disabled={busy || !data.enabled} className="at-btn" data-testid="backup-run-now">
          <RefreshCw size={14} /> {busy ? "Working…" : "Run backup now"}
        </button>
      </div>
      <div className="at-card p-6 mb-6" data-testid="backup-status">
        <div className="grid md:grid-cols-4 gap-6">
          <div>
            <div className="eyebrow">Encryption</div>
            <div className="mt-1 text-lg font-display font-black" style={{ color: data.enabled ? "var(--kiwi)" : "var(--clay)" }} data-testid="backup-status-enabled">
              {data.enabled ? "ENABLED" : "DISABLED"}
            </div>
            {!data.enabled && <div className="text-xs mt-1" style={{ color: "var(--clay)" }}>{data.reason}</div>}
          </div>
          <div>
            <div className="eyebrow">Retention</div>
            <div className="mt-1 text-lg font-display font-black">Keep newest {data.retention_count}</div>
          </div>
          <div>
            <div className="eyebrow">S3 mirror</div>
            <div className="mt-1 text-sm font-display font-bold" style={{ color: data.s3_configured ? "var(--kiwi)" : "var(--muted)" }}>
              {data.s3_configured ? data.s3_bucket : "Not configured"}
            </div>
          </div>
          <div>
            <div className="eyebrow">Excluded</div>
            <div className="mt-1 text-sm font-mono" style={{ color: "var(--muted)" }}>{(data.excluded_collections || []).join(", ") || "—"}</div>
          </div>
        </div>
      </div>
      <div className="at-card">
        <table className="at-tbl">
          <thead><tr><th>Created</th><th>Filename</th><th>Size</th><th>Rows</th><th>S3</th><th>Pruned</th><th></th></tr></thead>
          <tbody>
            {data.items.map(m => (
              <tr key={m.id} data-testid={`backup-row-${m.id}`}>
                <td className="font-bold whitespace-nowrap">{formatDate(m.created_at)}</td>
                <td className="font-mono text-xs" style={{ color: "var(--muted)" }}>{m.filename.replace("backups/", "")}</td>
                <td className="whitespace-nowrap">{formatBytes(m.bytes_encrypted)}<div className="text-xs" style={{ color: "var(--muted)" }}>{formatBytes(m.bytes_plaintext)} plain</div></td>
                <td className="whitespace-nowrap">
                  <span className="font-bold">{rowCountTotal(m.row_counts)}</span>
                  <div className="text-xs" style={{ color: "var(--muted)" }}>
                    {m.row_counts ? (Object.entries(m.row_counts).filter(([, n]) => n > 0).map(([k, n]) => `${k}=${n}`).join(" · ") || "empty") : ""}
                  </div>
                </td>
                <td>
                  {m.s3_uploaded === true ? <span style={{ color: "var(--kiwi)" }}>✓</span>
                    : m.s3_uploaded === false ? <span style={{ color: "var(--clay)" }} title={m.s3_error || ""}>✗</span>
                      : <span style={{ color: "var(--muted)" }}>—</span>}
                </td>
                <td>{m.pruned_at ? <span style={{ color: "var(--muted)" }} className="text-xs">yes</span> : ""}</td>
                <td className="text-right">
                  {isSuper && !m.pruned_at && (
                    <button onClick={() => openRestore(m)} disabled={busy} className="at-btn ghost xs" data-testid={`backup-restore-${m.id}`}>
                      <ShieldAlert size={12} /> Restore
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {data.items.length === 0 && <tr><td colSpan={7} className="p-8 text-center font-serif italic" style={{ color: "var(--muted)" }}>{data.enabled ? 'No backups yet. Click "Run backup now" to create the first one.' : "Backups are disabled. Set BACKUP_ENCRYPTION_KEY in the environment and restart."}</td></tr>}
          </tbody>
        </table>
      </div>
      {restoring && (
        <div className="overlay" onClick={() => !busy && setRestoring(null)} data-testid="backup-restore-modal">
          <div className="sheet" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="sheet-head">
              <div className="sheet-title">Restore <em>backup</em></div>
              <button onClick={() => !busy && setRestoring(null)}><X size={22} /></button>
            </div>
            <div className="sheet-body">
              <div className="p-4 border-2 rounded" style={{ borderColor: "var(--clay)", background: "#f4e0d8", color: "var(--clay)" }} data-testid="backup-restore-warning">
                <div className="font-display font-bold uppercase tracking-wide text-sm">This is destructive.</div>
                <div className="mt-2 text-sm font-serif">
                  Restoring will <b>replace every backed-up collection</b> with the contents of this file. Any data written since {formatDate(restoring.m.created_at)} will be permanently lost.
                </div>
              </div>
              <div className="mt-4 font-serif">To confirm, type the following exactly (case-sensitive):</div>
              <div className="mt-2 p-3 border-2 rounded font-mono text-sm break-all" style={{ borderColor: "var(--ink)", background: "var(--cream-3)" }} data-testid="backup-restore-expected">{restoring.expected}</div>
              <input className="at-input mt-3" value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="Type the confirmation string above" data-testid="backup-restore-confirm-input" autoFocus />
              <div className="mt-4 flex gap-2">
                <button onClick={doRestore} disabled={busy || confirmText !== restoring.expected} className="at-btn" data-testid="backup-restore-submit" style={confirmText === restoring.expected ? { background: "var(--clay)", borderColor: "var(--clay)", color: "var(--cream)" } : {}}>
                  {busy ? "Restoring…" : "Restore now"}
                </button>
                <button onClick={() => !busy && setRestoring(null)} className="at-btn ghost" data-testid="backup-restore-cancel">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function AdminConsole() {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const refreshUnread = () => api.get("/admin/dashboard").then(r => setUnread(r.data.unread_support));
  useEffect(() => { refreshUnread(); const t = setInterval(refreshUnread, 30000); return () => clearInterval(t); }, []);
  return (
    <div className="at-shell">
      <Sidebar user={user} unread={unread} />
      <main className="at-main">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="clients" element={<Clients />} />
          <Route path="queries" element={<Queries />} />
          <Route path="payments" element={<AdminPayments />} />
          <Route path="invoices" element={<AdminInvoices />} />
          <Route path="backups" element={<Backups />} />
          <Route path="support" element={<AdminSupport refreshUnread={refreshUnread} />} />
        </Routes>
      </main>
    </div>
  );
}
